import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react"
import {useSelector} from "react-redux";
import {useCheckoutProvider} from "components/Checkout/CheckoutProvider";
import {
    authorizeWithToken,
    authorizeWithTokenLikePlusGamb,
    authorizeWithTokenResend,
    authorizeWithTokenMileage,
    authorizeWithTokenPix,
    authorizeWithTokenConcierge
} from "Services/Apis/2FA";
import {useNavigate} from "react-router";
import {extractNumber} from "Utils/onlyNumbers";

export const MILEAGE_TOKEN_TYPE = 'mileage'

const Context = createContext({
    couldSendToken: true,
    secondFactorAuthConfirmated: false,
    timeUp: false,
    resend: true,
    tryCounter: 1,
    showModal: false,
    changeState: () => {
        throw new Error('Contexto não atribuido!')
    },
    handleShowModal: () => {
        throw new Error('Contexto não atribuido!')
    },
    handleCloseModal: () => {
        throw new Error('Contexto não atribuido!')
    },
    sendSMS: () => {
        throw new Error('Contexto não atribuido!')
    },
    resendSMS: () => {
        throw new Error('Contexto não atribuido!')
    },
    GotoConfirmation: () => {
        throw new Error('Contexto não atribuido!')
    },
    GoToCart: () => {
        throw new Error('Contexto não atribuido!')
    },
})

export const use2FAContext = () => useContext(Context)

const Checkout2FAModalProvider = ({ children, propsOrderId = null, propsStatus = null, propsServiceName = null, purchaseData = null, typeToken = null }) => {
    const [ showModal, setShowModal ] = useState(false)
    const [ couldSendToken, setCouldSendToken ] = useState(true)
    const [ resend, setResend ] = useState(true)
    const [ secondFactorAuthConfirmated, setSecondFactorAuthConfirmated ] = useState(false)
    const [ timeUp, setTimeUp ] = useState(false)
    const [ tryCounter, setTryCounter ] = useState(1)
    const [ orderRefer, setOrderRefer ] = useState(null)
    const { handleAddErrors } = useCheckoutProvider()
    const {
        CreatePurchase: {
            status: statusCode,
            createPurchase
        },
        FuelCredit: {
            status: statusCodeFuel,
            order: fuelOrder
        },
        Catalog: {
            onlyCash,
            id: catalogId
        },
        PaymentConfig: { paymentConfig },
        Cart: { cart },
        Shipping: { value: ShippingValue },
        oidc: { user }
    } = useSelector(({ oidc, CreatePurchase, Catalog, PaymentConfig, Cart, Shipping, FuelCredit,}) => ({
        CreatePurchase,
        Catalog,
        PaymentConfig,
        Cart,
        Shipping,
        oidc,
        FuelCredit
    }))
    const { credicard } = useCheckoutProvider()
    const AuthorizeWithToken = authorizeWithToken(user)
    const AuthorizeWithTokenGamb = authorizeWithTokenLikePlusGamb(user)
    const AuthorizeWithTokenPix = authorizeWithTokenPix(user)
    const AuthorizeWithTokenResend = authorizeWithTokenResend(user)
    const AuthorizeWithTokenMileage = authorizeWithTokenMileage(user, purchaseData?.supplierId || 117)
    const AuthorizeWithTokenConcierge = authorizeWithTokenConcierge()
    
    const Navigate = useNavigate()

    const checkStatusCode = useCallback(() => {
        let response = null;
        switch (406) {
            case statusCode:
                response = [406, 'CHECKOUT'];
                break;
            case statusCodeFuel:
                response = [406, 'FUEL'];
                break;
            case propsStatus:
                response = [406, propsServiceName];
                break;

        }

        switch ('RequiresTokenValidation') {
            case statusCode:
                response = ['RequiresTokenValidation', 'CHECKOUT'];
                break;
            case statusCodeFuel:
                response = ['RequiresTokenValidation', 'FUEL'];
                break;
            case propsStatus:
                response = ['RequiresTokenValidation', propsServiceName];
                break;
        }

        switch ('AuthenticationRequired') {
            case propsStatus:
                response = ['RequiresTokenValidation', propsServiceName];
                break;
        }

        return response
    }, [ statusCode, statusCodeFuel, propsStatus, propsServiceName ])

    const GoToCart = useCallback((type = 1) => {
        if (!checkStatusCode) return;
        const [_, serviceName] = checkStatusCode()
        switch (type) {
            case 2:
                if (serviceName === 'CHECKOUT') Navigate('/cart?2facancel=true')
                if (serviceName === 'BILLPAYMENT') {
                    handleCloseModal()
                    handleAddErrors('billPayment2FACancel', {
                        title: 'Desculpe, o código de 2FA não pôde ser validado, impedindo a conclusão do pedido.',
                        message: 'Por favor, verifique e tente novamente. Agradecemos sua compreensão.'
                    })
                }
                if (serviceName === 'RECHARGE_CELLPHONE') {
                    handleCloseModal()
                    handleAddErrors('RechargeCellphone2FACancel', {
                        title: 'Desculpe, o código de 2FA não pôde ser validado, impedindo a conclusão do pedido.',
                        message: 'Por favor, verifique e tente novamente. Agradecemos sua compreensão.'
                    })
                }

                if (serviceName === 'LIKEPLUS') {
                    handleCloseModal()
                    handleAddErrors('likeplus2FACancel', {
                        title: 'Desculpe, o código de 2FA não pôde ser validado, impedindo a conclusão do pedido.',
                        message: 'Por favor, verifique e tente novamente. Agradecemos sua compreensão.'
                    })
                }

                if (serviceName === 'FUELCREDIT') {
                    handleCloseModal()
                    handleAddErrors('fuelCredit2FACancel', {
                        title: 'Desculpe, o código de 2FA não pôde ser validado, impedindo a conclusão do pedido.',
                        message: 'Por favor, verifique e tente novamente. Agradecemos sua compreensão.'
                    })
                }

                if (serviceName === 'GETPLUSYOUR' || serviceName === 'GETPLUSYOURONLINE') {
                    handleCloseModal()
                    handleAddErrors('likePlusGetYour2FA', {
                        title: 'Desculpe, o código de 2FA não pôde ser validado, impedindo a conclusão do pedido.',
                        message: 'Por favor, verifique e tente novamente. Agradecemos sua compreensão.'
                    })
                }

                if (serviceName === 'PIX_2FA_VALIDATION'){
                    handleCloseModal()
                    handleAddErrors('pix2FACancel', {
                        title: 'Desculpe, o código de 2FA não pôde ser validado, impedindo a conclusão do pedido.',
                        message: 'Por favor, verifique e tente novamente. Agradecemos sua compreensão.'
                    })
                }
                  if (serviceName === 'RECHARGEPLUS') {
                    handleCloseModal();
                    handleAddErrors('likePlusRecharge2FA', {
                        title:'Desculpe, o código de 2FA não pôde ser validado, impedindo a conclusão do pedido.',
                        message: 'Por favor, verifique e tente novamente. Agradecemos sua compreensão.',
                    });
                }

                if (serviceName === 'CONCIERGE') {
                    handleCloseModal();
                    handleAddErrors('concierge2FA', {
                        title: 'Desculpe, o código de 2FA não pôde ser validado, impedindo a conclusão do resgate.',
                        message: 'Por favor, verifique e tente novamente. Agradecemos sua compreensão.',
                    });
                }
                break;
            default:
                if (serviceName === 'CHECKOUT') Navigate('/cart?2faerror=true')
                if (serviceName === 'BILLPAYMENT') {
                    handleCloseModal()
                    handleAddErrors('billPayment2FACancel', {
                        title: 'Parece que você atingiu o limite de tentativas.',
                        message: 'Não se preocupe, respire fundo e tente novamente mais tarde.😊'
                    })
                }
                if (serviceName === 'RECHARGE_CELLPHONE') {
                    handleCloseModal()
                    handleAddErrors('RechargeCellphone2FACancel', {
                        title: 'Parece que você atingiu o limite de tentativas.',
                        message: 'Não se preocupe, respire fundo e tente novamente mais tarde.😊'
                    })
                }
                if (serviceName === 'LIKEPLUS') {
                    handleCloseModal()
                    handleAddErrors('likeplus2FACancel', {
                        title: 'Parece que você atingiu o limite de tentativas.',
                        message: 'Não se preocupe, respire fundo e tente novamente mais tarde.😊'
                    })
                }
                if (serviceName === 'FUELCREDIT') {
                    handleCloseModal()
                    handleAddErrors('fuelCredit2FACancel', {
                        title: 'Parece que você atingiu o limite de tentativas.',
                        message: 'Não se preocupe, respire fundo e tente novamente mais tarde.😊'
                    })
                }

                if (serviceName === 'GETPLUSYOUR' || serviceName === 'GETPLUSYOURONLINE') {
                    handleCloseModal()
                    handleAddErrors('likePlusGetYour2FA', {
                        title: 'Parece que você atingiu o limite de tentativas.',
                        message: 'Não se preocupe, respire fundo e tente novamente mais tarde.😊'
                    })
                }

                if (serviceName === 'PIX_2FA_VALIDATION'){
                    handleCloseModal()
                    handleAddErrors('pix2FACancel', {
                        title: 'Parece que você atingiu o limite de tentativas.',
                        message: 'Não se preocupe, respire fundo e tente novamente mais tarde.😊'
                    })
                }

                if (serviceName === 'RECHARGEPLUS') {
                    handleCloseModal()
                    handleAddErrors('likePlusRecharge2FA', {
                        title: 'Parece que você atingiu o limite de tentativas.',
                        message: 'Não se preocupe, respire fundo e tente novamente mais tarde.😊'
                    })
                }

                if (serviceName === 'CONCIERGE') {
                    handleCloseModal()
                    handleAddErrors('concierge2FA', {
                        title: 'Parece que você atingiu o limite de tentativas.',
                        message: 'Não se preocupe, respire fundo e tente novamente mais tarde.😊'
                    })
                }
                break;
        }
    }, [ checkStatusCode, handleAddErrors ])

    const getOrderId = useCallback((nameOfStateRedux) => {
        switch (nameOfStateRedux) {
            case 'CHECKOUT':
                return createPurchase.id;
            case 'FUEL':
                return fuelOrder?.liveOrderId
            case propsServiceName:
                return propsOrderId
        }
    }, [ createPurchase.id, fuelOrder, propsServiceName, propsOrderId ])

    const GotoConfirmation = useCallback(() => {
        if (!checkStatusCode()) return;
        const [ _, nameReduxRefer ] = checkStatusCode()
        const orderId = getOrderId(nameReduxRefer)
        Navigate(`/order-details?orderId=${ orderId }&fromCheckout=true`)
    }, [ propsOrderId, fuelOrder, createPurchase, getOrderId ])

    const handleShowModal = useCallback(() => {
        if (!showModal) {
            setShowModal(true);
        }
    }, [ showModal ])

    const handleCloseModal = useCallback(() => {
        if (showModal) {
            setTryCounter(1)
            setShowModal(false);
        }
    }, [ showModal, tryCounter ])

    const PurchaseMoneyRequest = useCallback(() => {
        if (createPurchase) {
            const [ CardExpirationMonthDate, CardExpirationYearDate ] = credicard.validityDate.split('/');
            return {
                CardBrandId: credicard.brand.id,
                CardExpirationMonthDate,
                CardExpirationYearDate,
                CardNumber: credicard.number.replace(/\s/g, ''),
                CardHolderCpfCnpj: extractNumber(credicard.cpfNumber),
                CatalogId: catalogId,
                NameOfCardHolder: credicard.personName,
                Parcels: credicard.installments,
                PointsCash: credicard.pointsValue,
                SecurityCode: credicard.cvv,
                CatalogCardBrand: {
                    ...credicard.brand,
                    PaymentCode: credicard.cvv
                }
            }
        } else {
            return false;
        }
    }, [
        createPurchase,
        credicard,
        onlyCash,
        catalogId,
        paymentConfig,
        cart,
        ShippingValue
    ])

    const sendSMS = useCallback((token) => {
        if (!checkStatusCode()) return;
        const [ status, nameReduxRefer ] = checkStatusCode()
        if (status && (purchaseData === null || !purchaseData)) {
            return AuthorizeWithToken(
                getOrderId(nameReduxRefer),
                (credicard) ? PurchaseMoneyRequest(): null,
                token,
                1,
                false
            )
        } else if (typeToken === MILEAGE_TOKEN_TYPE) {
            return AuthorizeWithTokenMileage(getOrderId(nameReduxRefer), token, purchaseData?.mileageId || '')
        } else if (status && purchaseData && propsServiceName === 'PIX_2FA_VALIDATION') {
            return AuthorizeWithTokenPix(
                purchaseData,
                getOrderId(nameReduxRefer),
                token,
            )
        } else if (status && propsServiceName === 'CONCIERGE') {
            return AuthorizeWithTokenConcierge(
                purchaseData,
                getOrderId(nameReduxRefer),
                token,
            )
        } else if (status && purchaseData) {
            return AuthorizeWithTokenGamb(
                purchaseData,
                getOrderId(nameReduxRefer),
                token,
            )
        }
        return false;
    }, [ PurchaseMoneyRequest, createPurchase, fuelOrder, checkStatusCode, getOrderId, purchaseData ])

    const resendSMS = useCallback((token, type = 1) => {
        if (!checkStatusCode()) return;
        const [ status, nameReduxRefer ] = checkStatusCode()
        if (status) {
            return AuthorizeWithTokenResend(
                getOrderId(nameReduxRefer),
                (credicard) ? PurchaseMoneyRequest(): null,
                token,
                type,
                true
            )
        }
        return false;
    }, [ PurchaseMoneyRequest, createPurchase, fuelOrder, checkStatusCode, getOrderId ])


    const changeState = useCallback((stateName, value) => {
        const statesMethods = {
            couldSendToken: setCouldSendToken,
            resend: setResend,
            secondFactorAuthConfirmated: setSecondFactorAuthConfirmated,
            timeUp: setTimeUp,
            tryCounter: setTryCounter
        }

        if (statesMethods[stateName]) statesMethods[stateName](value)
        return value
    }, [
        couldSendToken,
        resend,
        secondFactorAuthConfirmated,
        timeUp,
        tryCounter
    ])

    const value = useMemo(() => ({
        couldSendToken,
        resend,
        secondFactorAuthConfirmated,
        timeUp,
        tryCounter,
        changeState,
        handleShowModal,
        showModal,
        sendSMS,
        handleCloseModal,
        GotoConfirmation,
        GoToCart,
        resendSMS
    }), [
        couldSendToken,
        resend,
        secondFactorAuthConfirmated,
        timeUp,
        tryCounter,
        changeState,
        handleShowModal,
        showModal,
        sendSMS,
        resendSMS,
        handleCloseModal,
        GotoConfirmation,
        GoToCart
    ])

    useEffect(() => {
        if (!checkStatusCode()) return;

        const [ status, nameReduxRefer ] = checkStatusCode()
        if (checkStatusCode() &&
            orderRefer !== getOrderId(nameReduxRefer)
        ) {
            handleShowModal()
            setOrderRefer(getOrderId(nameReduxRefer))
            changeState('secondFactorAuthConfirmated', (status !== 406))
        }
    }, [
        statusCode,
        createPurchase,
        handleShowModal,
        orderRefer,
        fuelOrder,
        checkStatusCode,
        getOrderId,
        propsServiceName
    ]);

    return (
        <Context.Provider value={value}>
            { children }
        </Context.Provider>
    )
}

export default Checkout2FAModalProvider
